/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { FC } from 'react';
import { Typography } from '@mui/material';

import { Activity, ActivityStatus } from '../../api/types/types';
import TestIds from '../../../../testIds';
import Modal from '../../../../components/Modal';
import { useUpdateActivity } from '../../api/hooks/useActivities';
import { selectCurrentAccountId } from '../../../azure-b2c/storage';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ACTIVITIES_ROUTE } from '../../activities-routes';
import { useNavigate } from 'react-router-dom';
import ActivityModalActionBar from '../ActivityModalActionBar/ActivityModalActionBar';

export interface CancelActivityModalProps {
  open: boolean;
  activity: Activity;
  onClose: () => void;
}

const CancelActivityModal: FC<CancelActivityModalProps> = ({
  open,
  activity,
  onClose,
}): JSX.Element => {
  const navigate = useNavigate();
  const accountNumber = useSelector(selectCurrentAccountId) ?? '';
  const { mutateAsync, isPending } = useUpdateActivity(accountNumber);

  const cancelActivity = (): void => {
    mutateAsync({
      ...activity,
      status: ActivityStatus.Canceled,
    })
      .then(() => {
        toast.success('Activity cancelled successfully');
        onClose();
        navigate(ACTIVITIES_ROUTE);
      })
      .catch(() => {
        toast.error('Failed to cancel activity. Please try again.');
      });
  };

  const handleClose = (): void => {
    if (!isPending) {
      onClose();
    }
  };

  return (
    <form>
      <Modal
        disableMobileFullScreen={true}
        title='Cancel Activity?'
        open={open}
        handleClose={handleClose}
        testId={TestIds.feat.activities.cancelActivityModal.component}
      >
        <Typography variant='body2'>
          You will no longer be able to edit '{activity.subject}' and it will be removed from
          FarmlandsPRO and Microsoft CE. Are you sure you want to continue?
        </Typography>

        <ActivityModalActionBar
          showLoadingIndicator={isPending}
          loadingIndicatorText='Cancelling activity'
          loadingIndicatorTestId={TestIds.feat.activities.cancelActivityModal.loading}
          closeButtonText='Close'
          closeButtonTestId={TestIds.feat.activities.cancelActivityModal.closeButton}
          submitButtonText='Cancel Activity'
          submitButtonTestId={TestIds.feat.activities.cancelActivityModal.submitButton}
          showDivider={false}
          onClose={onClose}
          onSubmit={cancelActivity}
        />
      </Modal>
    </form>
  );
};

export default CancelActivityModal;
